// frontend/src/components/SurveyBuilder.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './SurveyBuilder.css';
import { Tabs, Tab } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'kz', name: 'Қазақша' },
  { code: 'ru', name: 'Русский' },
];

const QuestionList = React.memo(({ questions, updateQuestion, deleteQuestion, handleKeyDown, moveQuestion }) => {
  const sortedQuestions = [...questions].sort((a, b) => a.order - b.order);
  const [animatingQuestions, setAnimatingQuestions] = useState(new Set());
  
  const handleMove = useCallback((index, direction) => {
    const currentId = sortedQuestions[index].id;
    const targetId = sortedQuestions[direction === 'up' ? index - 1 : index + 1].id;
    
    // Set animating state
    setAnimatingQuestions(new Set([currentId, targetId]));
    
    // Add animation classes
    const currentElement = document.querySelector(`[data-question-id="${currentId}"]`);
    const targetElement = document.querySelector(`[data-question-id="${targetId}"]`);
    
    if (currentElement && targetElement) {
      currentElement.classList.add(direction === 'up' ? 'question-moving-up' : 'question-moving-down');
      targetElement.classList.add(direction === 'up' ? 'question-moving-down' : 'question-moving-up');
      
      // Remove animation classes and perform the actual move after animation
      setTimeout(() => {
        currentElement.classList.remove('question-moving-up', 'question-moving-down');
        targetElement.classList.remove('question-moving-up', 'question-moving-down');
        setAnimatingQuestions(new Set());
        moveQuestion(index, direction);
      }, 300); // Match this with CSS animation duration
    }
  }, [sortedQuestions, moveQuestion]);

  return (
    <div className="questions-list">
      {sortedQuestions.map((q, index) => (
        <div 
          className="question-group"
          key={q.id}
          data-question-id={q.id}
          style={{
            opacity: animatingQuestions.has(q.id) ? 0 : 1,
            transition: 'opacity 0.3s ease'
          }}
        >
          <div className="question-item-wrapper">
            <div className="question-item">
              <div className="question-number">{index + 1}</div>
              <input
                type="text"
                value={q.text}
                onChange={(e) => updateQuestion(q.id, { text: e.target.value }, false)}
                onKeyDown={(e) => handleKeyDown(e, q.id, { text: e.target.value })}
                onBlur={() => updateQuestion(q.id, { text: q.text }, true)}
                className="question-creation-text"
                id={`question-${q.id}`}
                name={`question-${q.id}`}
              />
              <select
                value={q.question_type}
                onChange={(e) => updateQuestion(q.id, { question_type: e.target.value })}
                className="question-type"
                id={`question-type-${q.id}`}
                name={`question-type-${q.id}`}
              >
                <option value="open ended">Open Ended</option>
                <option value="single select" disabled>Single Select (Coming Soon)</option>
                <option value="multi select" disabled>Multi Select (Coming Soon)</option>
                <option value="rating scale">Rating Scale</option>
              </select>
              <button onClick={() => deleteQuestion(q.id)} className="delete-question">×</button>
            </div>
            {q.question_type === 'rating scale' && (
              <div className="rating-scale-config">
                <div className="rating-scale-row">
                  <div className="rating-buttons">
                    {[...Array(q.max_scale || 5)].map((_, i) => (
                      <button key={i} className="rating-number-button">
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <div className="rating-scale-text">
                    <span>From 1 to</span>
                    <input
                      type="number"
                      min="2"
                      max="10"
                      value={q.max_scale || 5}
                      onChange={(e) => updateQuestion(q.id, { max_scale: parseInt(e.target.value) || 5 }, false)}
                      onBlur={(e) => updateQuestion(q.id, { max_scale: parseInt(e.target.value) || 5 }, true)}
                      className="max-scale-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="question-controls">
            <button
              onClick={() => handleMove(index, 'up')}
              disabled={index === 0 || animatingQuestions.size > 0}
              className="move-question"
            >
              ↑
            </button>
            <button
              onClick={() => handleMove(index, 'down')}
              disabled={index === questions.length - 1 || animatingQuestions.size > 0}
              className="move-question"
            >
              ↓
            </button>
          </div>
        </div>
      ))}
    </div>
  );
});

function SurveyBuilder() {
  const { id: projectId } = useParams();
  const [projectDetails, setProjectDetails] = useState({
    survey_objective: '',
    additional_context: '',
    welcome_heading: '',
    welcome_message: '',
    goodbye_message: '',
  });
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLanguages, setSelectedLanguages] = useState(['en']);
  const [translations, setTranslations] = useState({});
  const [activeTab, setActiveTab] = useState('default');

  const fetchProjectDetails = useCallback(() => {
    setIsLoading(true);
    api.get(`/projects/${projectId}`)
      .then(response => {
        setProjectDetails(response.data);
        setIsLoading(false);
      })
      .catch(error => {
        // console.error(error);
        setError('Failed to load project details.');
        setIsLoading(false);
      });
  }, [projectId]);

  const fetchQuestions = useCallback(() => {
    setIsLoading(true);
    api.get(`/projects/${projectId}/preview`)
      .then(response => {
        if (response.data && response.data.questions) {
          setQuestions(response.data.questions);
          setProjectDetails(prevDetails => ({
            ...prevDetails,
            survey_objective: response.data.survey_objective,
            additional_context: response.data.additional_context,
            welcome_heading: response.data.welcome_heading,
            welcome_message: response.data.welcome_message,
            goodbye_message: response.data.goodbye_message
          }));
        }
        setIsLoading(false);
      })
      .catch(error => {
        // console.error(error);
        setError('Failed to load project questions.');
        setQuestions([]);
        setIsLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    fetchProjectDetails();
    fetchQuestions();
  }, [fetchProjectDetails, fetchQuestions]);

  useEffect(() => {
    api.get(`/projects/${projectId}/translations`)
      .then(response => {
        setTranslations(response.data);
        setSelectedLanguages(Object.keys(response.data));
      })
      .catch(error => console.error('Failed to load translations:', error));
  }, [projectId]);

  const updateProjectDetails = useCallback((field, value) => {
    // Update local state
    setProjectDetails(prevDetails => ({
        ...prevDetails,
        [field]: value
    }));

    // Update project details in backend
    api.put(`/projects/${projectId}`, { [field]: value })
        .then(() => {
            // Update English translation in local state
            setTranslations(prev => ({
                ...prev,
                en: {
                    ...prev.en,
                    [field]: value
                }
            }));

            // Update English translation in backend
            return api.put(`/projects/${projectId}/translations`, {
                language_code: 'en',
                translation: {
                    welcome_heading: field === 'welcome_heading' ? value : translations.en?.welcome_heading || '',
                    welcome_message: field === 'welcome_message' ? value : translations.en?.welcome_message || '',
                    goodbye_message: field === 'goodbye_message' ? value : translations.en?.goodbye_message || ''
                }
            });
        })
        .catch(error => {
            // console.error('Failed to update project details:', error);
            setError('Failed to update project details. Please try again.');
        });
  }, [projectId, translations]);

  const addQuestion = useCallback(() => {
    setIsLoading(true);
    const newQuestionOrder = questions.length + 1;
    api.post(`/projects/${projectId}/questions`, { 
      text: '', 
      question_type: 'open ended',
      order: newQuestionOrder
    })
      .then(response => {
        if (response.data && response.data.question_id) {
          const newQuestion = {
            id: response.data.question_id,
            text: '',
            question_type: 'open ended',
            order: newQuestionOrder
          };
          setQuestions(prevQuestions => [...prevQuestions, newQuestion]);
          
          // Focus on the new question input after a short delay
          setTimeout(() => {
            const newQuestionInput = document.getElementById(`question-${response.data.question_id}`);
            if (newQuestionInput) {
              newQuestionInput.focus();
            }
          }, 0);
        } else {
          throw new Error('Server response did not include a valid question ID');
        }
      })
      .catch(error => {
        // console.error('Failed to add question:', error);
        setError('Failed to add question. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [projectId, questions.length]);

  const updateQuestion = useCallback((id, updatedData, shouldUpdateServer = true) => {
    if (!id) {
      // console.error('Question ID is undefined');
      setError('Failed to update question: Invalid question ID');
      return;
    }

    setQuestions(prevQuestions =>
      prevQuestions.map(q => q.id === id ? { ...q, ...updatedData } : q)
    );

    if (shouldUpdateServer) {
      api.put(`/projects/${projectId}/questions/${id}`, updatedData)
        .catch(error => {
          // console.error('Failed to update question:', error);
          setError('Failed to update question. Please try again.');
        });
    }
  }, [projectId]);

  const deleteQuestion = useCallback((id) => {
    api.delete(`/projects/${projectId}/questions/${id}`)
      .then(() => {
        setQuestions(prevQuestions => prevQuestions.filter(q => q.id !== id));
      })
      .catch(error => {
        // console.error('Failed to delete question:', error);
        setError('Failed to delete question. Please try again.');
      });
  }, [projectId]);

  const handleKeyDown = useCallback((event, id, updatedData) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (id) {
        updateQuestion(id, updatedData, true);
      } else {
        // console.error('Cannot update question: ID is undefined');
        setError('Failed to update question: Invalid question ID');
      }
    }
  }, [updateQuestion]);

  const moveQuestion = useCallback((index, direction) => {
    const newQuestions = [...questions];
    if (direction === 'up' && index > 0) {
      [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
    } else if (direction === 'down' && index < newQuestions.length - 1) {
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
    } else {
      return; // No change needed
    }

    // Update the order of all questions
    newQuestions.forEach((q, i) => {
      q.order = i + 1;
    });

    // Update the state
    setQuestions(newQuestions);

    // Send the update to the server
    const updatedOrders = newQuestions.map(q => ({ id: q.id, order: q.order }));
    api.put(`/projects/${projectId}/questions/reorder`, { questions: updatedOrders })
      .then(() => {
        // console.log('Question order updated successfully');
      })
      .catch(error => {
        // console.error('Failed to update question order:', error);
        setError('Failed to update question order. Please try again.');
        // Revert the change in case of an error
        fetchQuestions();
      });
  }, [questions, projectId, fetchQuestions]);

  const updateTranslation = useCallback((lang) => {
    api.put(`/projects/${projectId}/translations`, {
      language_code: lang,
      translation: translations[lang] || {
        welcome_heading: '',
        welcome_message: '',
        goodbye_message: ''
      }
    })
      .then(response => {
        // console.log('Translation updated successfully:', response);
      })
      .catch(error => {
        // console.error('Failed to update translation:', error);
        setError('Failed to update translation. Please try again.');
      });
  }, [projectId, translations]);

  const handleLanguageChange = async (langCode, isChecked) => {
    try {
      if (isChecked) {
        // Adding a language
        await api.put(`/projects/${projectId}/translations`, {
          language_code: langCode,
          translation: {
            welcome_heading: '',
            welcome_message: '',
            goodbye_message: ''
          }
        });

        setSelectedLanguages(prev => [...prev, langCode]);
        setTranslations(prev => ({
          ...prev,
          [langCode]: {
            welcome_heading: '',
            welcome_message: '',
            goodbye_message: ''
          }
        }));
      } else {
        // Switch to default tab first if removing current tab
        if (activeTab === langCode) {
          setActiveTab('default');
        }

        // Remove language from API first
        await api.delete(`/projects/${projectId}/translations/${langCode}`);

        // Then update local state
        setSelectedLanguages(prev => prev.filter(l => l !== langCode));
        setTranslations(prev => {
          const newTranslations = { ...prev };
          delete newTranslations[langCode];
          return newTranslations;
        });
      }
    } catch (error) {
      // console.error('Failed to update language:', error);
      // Revert checkbox state on error
      if (isChecked) {
        setSelectedLanguages(prev => prev.filter(l => l !== langCode));
      } else {
        setSelectedLanguages(prev => [...prev, langCode]);
      }
      setError('Failed to update language selection. Please try again.');
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="survey-builder">
      <h3 className="section-title">
        Setup
        <Tooltip title="Define the objective and context for your survey" placement="right">
          <HelpOutlineIcon className="help-icon" fontSize="small" />
        </Tooltip>
      </h3>
      <div className="setup-section">
        <div className="setup-item">
          <div className="setup-label">Objective</div>
          <textarea
            value={projectDetails.survey_objective}
            onChange={(e) => updateProjectDetails('survey_objective', e.target.value)}
            className="borderless-input"
            placeholder="Survey objective"
            rows={2}
          />
        </div>
        <div className="setup-item">
          <div className="setup-label">Additional context (optional)</div>
          <textarea
            value={projectDetails.additional_context}
            onChange={(e) => updateProjectDetails('additional_context', e.target.value)}
            className="borderless-input"
            placeholder="Additional context"
            rows={2}
          />
        </div>
      </div>

      <h3 className="section-title">
        Welcome Screen & Languages
        <Tooltip title="Customize welcome and goodbye messages in multiple languages" placement="right">
          <HelpOutlineIcon className="help-icon" fontSize="small" />
        </Tooltip>
      </h3>
      <div className="welcome-screen-section">
        <div className="language-selector">
          {SUPPORTED_LANGUAGES.map(lang => (
            <label key={lang.code} className="language-option">
              <input
                type="checkbox"
                checked={selectedLanguages.includes(lang.code)}
                onChange={(e) => handleLanguageChange(lang.code, e.target.checked)}
                disabled={lang.code === 'en'}
              />
              <span className="language-label">
                {lang.name}
                {lang.code === 'en' && " (Default)"}
              </span>
            </label>
          ))}
        </div>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          className="language-tabs"
        >
          <Tab value="default" label="Default (English)" />
          {selectedLanguages
            .filter(lang => lang !== 'en')
            .map(lang => (
              <Tab
                key={lang}
                value={lang}
                label={SUPPORTED_LANGUAGES.find(l => l.code === lang).name}
              />
            ))}
        </Tabs>

        {activeTab === 'default' && (
          <div className="welcome-content">
            <div className="welcome-item">
              <input
                type="text"
                value={projectDetails.welcome_heading}
                onChange={(e) => updateProjectDetails('welcome_heading', e.target.value)}
                className="borderless-input"
                placeholder="Welcome heading"
              />
            </div>
            <div className="welcome-item">
              <textarea
                value={projectDetails.welcome_message}
                onChange={(e) => updateProjectDetails('welcome_message', e.target.value)}
                className="borderless-input"
                placeholder="Welcome message"
                rows={2}
              />
            </div>
            <div className="welcome-item">
              <textarea
                value={projectDetails.goodbye_message}
                onChange={(e) => updateProjectDetails('goodbye_message', e.target.value)}
                className="borderless-input"
                placeholder="Goodbye message"
                rows={2}
              />
            </div>
          </div>
        )}

        {activeTab !== 'default' && (
          <div className="welcome-content">
            <div className="welcome-item">
              <input
                type="text"
                value={translations[activeTab]?.welcome_heading ?? ''}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    [activeTab]: { ...translations[activeTab], welcome_heading: e.target.value }
                  });
                }}
                onBlur={() => updateTranslation(activeTab)}
                className="borderless-input"
                placeholder={`Welcome heading (${activeTab})`}
              />
            </div>
            <div className="welcome-item">
              <textarea
                value={translations[activeTab]?.welcome_message ?? ''}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    [activeTab]: { ...translations[activeTab], welcome_message: e.target.value }
                  });
                }}
                onBlur={() => updateTranslation(activeTab)}
                className="borderless-input"
                placeholder={`Welcome message (${activeTab})`}
                rows={2}
              />
            </div>
            <div className="welcome-item">
              <textarea
                value={translations[activeTab]?.goodbye_message ?? ''}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    [activeTab]: { ...translations[activeTab], goodbye_message: e.target.value }
                  });
                }}
                onBlur={() => updateTranslation(activeTab)}
                className="borderless-input"
                placeholder={`Goodbye message (${activeTab})`}
                rows={2}
              />
            </div>
          </div>
        )}
      </div>

      <h3 className="section-title">
        Conversation Guide
        <Tooltip title="Create and organize your survey questions" placement="right">
          <HelpOutlineIcon className="help-icon" fontSize="small" />
        </Tooltip>
      </h3>
      {error && <div className="error">{error}</div>}
      <QuestionList
        questions={questions}
        updateQuestion={updateQuestion}
        deleteQuestion={deleteQuestion}
        handleKeyDown={handleKeyDown}
        moveQuestion={moveQuestion}
      />
      <div className="question-item add-question" onClick={addQuestion}>
        <div className="add-question-text">Add New Question</div>
      </div>
    </div>
  );
}

export default SurveyBuilder;